import React, { useContext } from 'react'
import styled from 'styled-components'

import SurveyAnswerContext from '../../contexts/SurveyAnswerContext'
import { MysqlUsers } from '../../graphqlTypes'

/**
 * 都道府県の地域分類
 */
const REGIONS = [
  { prefectures: [1], value: '北海道' },
  { prefectures: [2, 3, 4, 5, 6, 7], value: '東北' },
  { prefectures: [8, 9, 10, 11, 12, 13, 14, 19, 20], value: '関東甲信' },
  { prefectures: [21, 22, 23, 24], value: '東海' },
  { prefectures: [15, 16, 17, 18], value: '北陸' },
  { prefectures: [25, 26, 27, 28, 29, 30], value: '近畿' },
  { prefectures: [31, 32, 33, 34], value: '中国' },
  { prefectures: [36, 37, 38, 39], value: '四国' },
  { prefectures: [35, 40, 41, 42, 42, 44], value: '九州北部' },
  { prefectures: [45, 46], value: '九州南部' },
  { prefectures: [47], value: '沖縄' },
]

const Container = styled.table`
  border: 1px solid ${({ theme }) => theme.palette.border.main};
  border-spacing: 0;
  margin: 22px 0 0;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 6px 0 0;
  }
`

const TableRow = styled.tr`
  color: ${({ theme }) => theme.palette.font.main};
  &:nth-child(odd) {
    background: ${({ theme }) => theme.palette.background.pale};
  }
`

const TableHeading = styled.th<{ last: boolean }>`
  border-right: 1px solid ${({ theme }) => theme.palette.border.light};
  border-bottom: ${({ last, theme }) => (last ? 0 : `1px solid ${theme.palette.border.light}`)};
  font-size: 10px;
  padding: 8px 10px;
  width: 130px;
`

const TableData = styled.td<{ last: boolean }>`
  border-bottom: ${({ last, theme }) => (last ? 0 : `1px solid ${theme.palette.border.light}`)};
  font-size: 10px;
  padding: 8px 10px;
`

type Props = {
  category: number
  user: MysqlUsers
}

export default function UserAttributeList({ category, user }: Props) {
  const { userAttributes } = useContext(SurveyAnswerContext)

  const userAttribute = userAttributes.find(({ categories }) => categories.includes(category))
  if (!userAttribute) return null

  return (
    <Container>
      <tbody>
        {userAttribute.columns.map((column, index) => {
          const last = userAttribute.columns.length === index + 1

          return (
            <TableRow key={index}>
              <TableHeading last={last}>
                {(() => {
                  switch (column) {
                    case 'prefecture_id':
                      return '地域'
                    case 'residential_area':
                      return 'エリア'
                    case 'building_type':
                      return '住宅'
                    case 'floor_plan':
                      return '間取り'
                    case 'noise_care':
                      return '育児に関する生活音'
                    case 'transport':
                      return '主な交通手段'
                    case 'car_model':
                      return '車種'
                    case 'step_existence':
                      return '自宅に出入りする際の障害物'
                    case 'support_member':
                      return '一緒に使用している人'
                    case 'mother_height':
                      return 'ママの身長'
                    case 'mother_weight':
                      return 'ママの体型'
                    case 'milk':
                      return '母乳 / ミルク'
                    case 'youngest_child_height':
                      return '子供服のサイズ(末子)'
                    case 'youngest_child_weight':
                      return '子供の体型(末子)'
                    default:
                      throw new TypeError(`"column" is not MysqlUsersField: ${column}`)
                  }
                })()}
              </TableHeading>
              <TableData last={last}>
                {(() => {
                  const value = user[column]
                  if (value == null) {
                    return '-'
                  }

                  if (typeof value === 'number' && column === 'prefecture_id') {
                    const region = REGIONS.find(({ prefectures }) => prefectures.includes(value))
                    return region ? region.value : '-'
                  }

                  if (typeof value === 'string' && column === 'building_type') {
                    return value.replace(/\s*\(.+\)/, '')
                  }

                  if (typeof value === 'string' && column === 'floor_plan' && value === 'それ以上') {
                    return '5K/DK/LDK以上'
                  }

                  return value
                })()}
              </TableData>
            </TableRow>
          )
        })}
      </tbody>
    </Container>
  )
}
